// web/src/pages/Landing.tsx
import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  type User,
} from "firebase/auth";
import { auth, db } from "@/lib/firebase";
import {
  collectionGroup,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type QueryDocumentSnapshot,
  type DocumentData,
  Timestamp,
} from "firebase/firestore";
import { ensureUserDoc } from "@/data/user";
import SmartLink from "@/components/SmartLink";

/* =========================
   Types
========================= */

type AuthStage = "idle" | "working";

type PublicCardTile = {
  path: string; // doc.ref.path (unique across collection group)
  id: string; // doc.id
  name: string;
  image?: string | null;
  set?: string | null;
  number?: string | null;
  status?: "sale" | "trade" | "keep" | null;
  for_sale?: boolean;
  for_trade?: boolean;
  price?: number | null;
  createdAt?: Timestamp | null;
};

/* =========================
   Helpers
========================= */

function isNewUser(u: User): boolean {
  try {
    const created = u.metadata.creationTime
      ? Date.parse(u.metadata.creationTime)
      : 0;
    const last = u.metadata.lastSignInTime
      ? Date.parse(u.metadata.lastSignInTime)
      : 0;
    return Math.abs(created - last) < 3_000;
  } catch {
    return false;
  }
}

function tsToMillis(ts?: Timestamp | null): number {
  if (!ts) return 0;
  // Firestore Timestamp has seconds & nanoseconds
  return ts.seconds * 1_000 + Math.floor(ts.nanoseconds / 1_000_000);
}

function parseAuthError(e: unknown): string {
  const msg = (e as { message?: string })?.message ?? "Authentication failed.";
  if (msg.includes("email-already-in-use"))
    return "That email is already in use.";
  if (msg.includes("invalid-email")) return "Please enter a valid email.";
  if (msg.includes("weak-password"))
    return "Password should be at least 6 characters.";
  if (msg.includes("wrong-password")) return "Incorrect password.";
  if (msg.includes("user-not-found"))
    return "No account found with that email.";
  return msg;
}

/* =========================
   Page
========================= */

declare global {
  interface Window {
    twttr?: { widgets?: { load: (el?: Element) => void } };
  }
}

export default function Landing(): JSX.Element {
  const [signupOpen, setSignupOpen] = useState<boolean>(false);
  const [loginOpen, setLoginOpen] = useState<boolean>(false);
  const [beltPaused, setBeltPaused] = useState<boolean>(false);

  // NEW: support banner visibility (persisted)
  const [showIggo, setShowIggo] = useState<boolean>(true);
  useEffect(() => {
    try {
      const hid = localStorage.getItem("hideIggo");
      if (hid === "1") setShowIggo(false);
    } catch {}
  }, []);
  function dismissIggo(): void {
    setShowIggo(false);
    try {
      localStorage.setItem("hideIggo", "1");
    } catch {}
  }

  // Hide top app header + bottom navbar only on this page
  useEffect(() => {
    if (typeof document === "undefined") return;

    const topSelectors = [
      'header[data-app-chrome="top"]',
      "#app-topbar",
      'header[role="banner"]',
      "header.sticky",
      ".app-topbar",
    ];
    const bottomSelectors = [
      'footer[data-app-chrome="bottom"]',
      "#bottomnav",
      'footer[role="contentinfo"]',
      "nav[data-bottom-nav]",
      ".bottomnav",
    ];

    type Hidden = { el: HTMLElement; prev: string };
    const hidden: Hidden[] = [];

    const hide = (sel: string): void => {
      const nodes = document.querySelectorAll(sel);
      nodes.forEach((n) => {
        const el = n as HTMLElement;
        // Save inline display style to restore later
        const prev = el.style.display;
        el.style.setProperty("display", "none", "important");
        el.setAttribute("aria-hidden", "true");
        hidden.push({ el, prev });
      });
    };

    [...topSelectors, ...bottomSelectors].forEach(hide);

    return () => {
      hidden.forEach(({ el, prev }) => {
        if (prev) el.style.display = prev;
        else el.style.removeProperty("display");
        el.removeAttribute("aria-hidden");
      });
    };
  }, []);

  // Lock body scroll when a modal is open (mobile-friendly)
  useEffect(() => {
    if (!signupOpen && !loginOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [signupOpen, loginOpen]);

  // Recent “market” strip state
  const [loadingMarket, setLoadingMarket] = useState<boolean>(true);
  const [market, setMarket] = useState<PublicCardTile[]>([]);
  const [marketErr, setMarketErr] = useState<string | null>(null);

  // Fetch the 5 most recent sale/trade cards across all users (collection group)
  useEffect(() => {
    let alive = true;

    async function load(): Promise<void> {
      setLoadingMarket(true);
      setMarketErr(null);
      try {
        const cg = collectionGroup(db, "cards");

        const qStatus = query(
          cg,
          where("status", "in", ["sale", "trade"]),
          orderBy("createdAt", "desc"),
          limit(10),
        );

        const qSale = query(
          cg,
          where("for_sale", "==", true),
          orderBy("createdAt", "desc"),
          limit(10),
        );

        const qTrade = query(
          cg,
          where("for_trade", "==", true),
          orderBy("createdAt", "desc"),
          limit(10),
        );

        const [sSnap, saleSnap, tradeSnap] = await Promise.all([
          getDocs(qStatus),
          getDocs(qSale),
          getDocs(qTrade),
        ]);

        const toTile = (
          d: QueryDocumentSnapshot<DocumentData>,
        ): PublicCardTile => {
          const v = d.data();
          return {
            path: d.ref.path,
            id: d.id,
            name: (v.name ?? "Unknown").toString(),
            image: (v.custom_image_url ?? v.image ?? null) || null,
            set: v.set ?? null,
            number: v.number ?? null,
            status: (v.status ?? null) as PublicCardTile["status"],
            for_sale: Boolean(v.for_sale),
            for_trade: Boolean(v.for_trade),
            price: typeof v.price === "number" ? v.price : null,
            createdAt: v.createdAt ?? null,
          };
        };

        const merged = [...sSnap.docs, ...saleSnap.docs, ...tradeSnap.docs].map(
          toTile,
        );

        // De-duplicate by doc path (unique globally)
        const map = new Map<string, PublicCardTile>();
        for (const tile of merged) {
          if (!map.has(tile.path)) map.set(tile.path, tile);
        }

        // Sort by createdAt desc and keep top 10
        const sorted = Array.from(map.values()).sort(
          (a, b) => tsToMillis(b.createdAt) - tsToMillis(a.createdAt),
        );
        const top10 = sorted.slice(0, 10);

        if (alive) setMarket(top10);
      } catch (e: unknown) {
        if (alive)
          setMarketErr(
            (e as { message?: string })?.message ?? "Failed to load.",
          );
      } finally {
        if (alive) setLoadingMarket(false);
      }
    }

    void load();
    return () => {
      alive = false;
    };
  }, []);

  // Load X (Twitter) widgets once and re-scan on mount
  useEffect(() => {
    if ((window as any).__TW_EMBED_LOADED__) return;
    (window as any).__TW_EMBED_LOADED__ = true;

    const ID = "x-widgets-js";
    const existing = document.getElementById(ID) as HTMLScriptElement | null;
    if (existing) {
      try { window.twttr?.widgets?.load(); } catch {}
      return;
    }
    const s = document.createElement("script");
    s.id = ID;
    s.async = true;
    s.src = "https://platform.twitter.com/widgets.js";
    s.onload = () => {
      try { window.twttr?.widgets?.load(); } catch {}
    };
    document.body.appendChild(s);
  }, []);

  return (
    <main className="min-h-screen bg-gradient-to-b from-[#0b0620] via-[#0a0420] to-[#02010f] pb-[env(safe-area-inset-bottom)]">
           
      {/* ===== Indiegogo Support Banner (TOP) ===== */}
      {showIggo && (
        <div
          className="mx-auto max-w-6xl px-4 pt-4"
          role="region"
          aria-label="Indiegogo campaign"
        >
          <div className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-600/10 backdrop-blur px-4 py-3 shadow-md">
            <div className="flex items-center gap-3">
              {/* CashApp image outside the SectionShell, aligned with the section */}
            <div className="shrink-0">
              <a
                  href="https://cash.app/$spellgather"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 inline-block"
                >
              <img
                src="/images/sg-cashapp.png"
                alt="Support via Cash App"
                className="h-64 w-auto rounded-md border border-white/0 object-contain"
              />
              </a>
            </div>
              <div className="flex-1 text-sm leading-5">
                <span className="font-semibold text-fuchsia-300">
                  Help power Spellgather’s launch:
                </span>{" "}
                We’re crowdfunding on Indiegogo to cover early build &
                infrastructure costs. Backers receive a special gift (while
                supplies last).
              </div>
              <div className="flex items-center gap-2">
                <a
                  href="https://www.indiegogo.com/projects/spellgather-launch-funding#/"
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center rounded-lg bg-fuchsia-500 px-3 py-2 text-sm font-semibold text-black hover:bg-fuchsia-400"
                >
                  Support
                </a>
                <button
                  type="button"
                  onClick={dismissIggo}
                  className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white/80 hover:bg-white/5"
                >
                  Dismiss
                </button>
              </div>
              
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:py-20 text-center">
        <div className="relative w-full">
          {/* enlarged logo container (still centered) */}
          <div className="mx-auto w-[260px] h-[260px] sm:w-[360px] sm:h-[360px] rounded-2xl overflow-visible">
            <img
              src="/images/spellgather-logo.png"
              alt="Spellgather logo"
              className="w-full h-full object-contain rounded-[6px] ring-8 ring-black/30 shadow-lg"
              loading="eager"
              decoding="async"
            />
          </div>

          {/* Title moved fully below the logo for clarity */}
          <div className="mt-6 sm:mt-8 px-4">
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Welcome to <span className="text-fuchsia-400">Spellgather</span>
            </h1>
            <p className="mt-3 text-white/80 max-w-full mx-auto">
              Trade smarter. Play together. Forge your legend.
            </p>
            <p> </p>
            <p> </p>
            <p className="mt-3 text-xs text-white/80 max-w-full mx-auto">
              Spellgather is not affiliated with or endorsed by Wizards of the
              Coast.
            </p>
          </div>
        </div>

        {/* Available on the Marketplace — continuous conveyor belt */}
        <div className="mt-20 text-left">
          <div className="mx-auto max-w-6xl">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-white/90 text-base font-semibold">
                Available locally in the Marketplace
              </h2>
              <span className="text-xs text-white/50"></span>
            </div>

            {/* Conveyor belt CSS */}
            <style>{`
              @keyframes belt-scroll {
                from { transform: translateX(0); }
                to   { transform: translateX(-50%); }
              }
              @media (prefers-reduced-motion: reduce) {
                .belt-track { animation: none !important; transform: translateX(0) !important; }
              }
            `}</style>

            <div className="mt-3 relative overflow-hidden">
              {/* Loading / error states */}
              {loadingMarket && (
                <div className="flex gap-3 pr-2">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div
                      key={`skeleton-${i}`}
                      className="h-[216px] sm:h-[260px] w-[140px] sm:w-[180px] rounded-lg border border-white/10 bg-white/5 animate-pulse"
                      aria-hidden="true"
                    />
                  ))}
                </div>
              )}
              {!loadingMarket && marketErr && (
                <div className="text-white/70 text-sm px-1">
                  Couldn’t load latest listings.
                </div>
              )}

              {/* Animated belt */}
              {!loadingMarket &&
                !marketErr &&
                market.length > 0 &&
                (() => {
                  const market10 = (() => {
                    const items: PublicCardTile[] = [...market];
                    if (items.length < 10) {
                      for (let i = 0; items.length < 10; i++)
                        items.push(market[i % market.length]);
                    }
                    return items.slice(0, 10);
                  })();
                  const loop = [...market10, ...market10]; // duplicate for seamless loop
                  const duration = `${Math.max(24, market10.length * 3)}s`;
                  return (
                    <div
                      className="group"
                      onMouseEnter={() => setBeltPaused(true)}
                      onMouseLeave={() => setBeltPaused(false)}
                      onTouchStart={() => setBeltPaused(true)}
                      onTouchEnd={() => setBeltPaused(false)}
                      onFocus={() => setBeltPaused(true)}
                      onBlur={() => setBeltPaused(false)}
                    >
                      <div
                        className="belt-track flex gap-3 pr-2 will-change-transform"
                        style={{
                          width: "200%",
                          animationName: "belt-scroll",
                          animationTimingFunction: "linear",
                          animationIterationCount: "infinite",
                          animationDuration: duration,
                          animationPlayState: beltPaused
                            ? ("paused" as const)
                            : ("running" as const),
                        }}
                        aria-live="off"
                      >
                        {loop.map((c, idx) => (
                          <div
                            key={`${c.path}-${idx}`}
                            role="button"
                            tabIndex={0}
                            className="relative text-left rounded-lg overflow-hidden border border-white/10 bg-black/20 hover:border-white/20 transition-colors cursor-pointer focus:outline-none flex-none w-[140px] sm:w-[180px]"
                            onClick={() => setSignupOpen(true)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSignupOpen(true);
                              }
                            }}
                            aria-label={`See ${c.name} — sign up to view details`}
                            title={c.name}
                          >
                            <div className="aspect-[3/4] bg-black">
                              {c.image ? (
                                <img
                                  src={c.image}
                                  alt={c.name}
                                  className="h-full w-full object-cover"
                                  loading="lazy"
                                  decoding="async"
                                />
                              ) : (
                                <div className="h-full w-full grid place-items-center text-white/70 text-xs">
                                  No image
                                </div>
                              )}
                            </div>
                            <div className="p-2">
                              <div
                                className="text-white text-xs font-medium truncate"
                                title={c.name}
                              >
                                {c.name}
                              </div>
                              <div className="text-white/60 text-[11px] truncate">
                                {c.set ?? ""}{" "}
                                {c.number ? `• #${c.number}` : ""}
                              </div>
                              <div className="mt-1 inline-flex items-center gap-1">
                                {badgeFor(c)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            id="landing-cta-signup"
            type="button"
            onClick={() => setSignupOpen(true)}
            className="group relative inline-flex items-center justify-center rounded-xl border border-fuchsia-500/50 bg-fuchsia-600/20 px-5 py-3 text-white font-semibold shadow-md hover:bg-fuchsia-600/30 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
            aria-haspopup="dialog"
          >
            <span className="transition-opacity group-hover:opacity-0">
              Join the ranks
            </span>
            <span className="absolute transition-opacity opacity-0 group-hover:opacity-100">
              Sign up
            </span>
          </button>

          <button
            type="button"
            onClick={() => setLoginOpen(true)}
            className="group relative inline-flex items-center justify-center rounded-xl border border-indigo-500/50 bg-indigo-600/20 px-5 py-3 text-white font-semibold shadow-md hover:bg-indigo-600/30 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            aria-haspopup="dialog"
          >
            <span className="transition-opacity group-hover:opacity-0">
              Continue the lore
            </span>
            <span className="absolute transition-opacity opacity-0 group-hover:opacity-100">
              Login
            </span>
          </button>
        </div>

        {/* Public browse: single read‑only Community Feed link */}
        <div className="mt-3 flex items-center justify-center" role="group" aria-label="Public browse">
          <Link
            to="/community/public"
            className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-white font-semibold hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
          >
            Explore the Community (Read‑only)
          </Link>
        </div>

        {/* ===== Social hub + embeds (AFTER CTAs) ===== */}
        <section className="mt-10 text-left" role="region" aria-label="Community & social">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Join the Spellgather community</h2>
            <p className="text-xs text-white/60">
              Follow for updates, events & trades
            </p>
          </div>

          {/* Icon row — hook up your profile URLs and pixel icons */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            <SmartLink
              to="https://x.com/spellgather"
              newTab
              className="group flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-3 hover:bg-white/10"
            >
              <img src="/images/icons/x.png" className="h-6 w-6" alt="X" />
              <span className="text-sm group-hover:underline">X</span>
            </SmartLink>

            <SmartLink
              to="https://instagram.com/spellgather"
              newTab
              className="group flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-3 hover:bg-white/10"
            >
              <img src="/images/icons/instagram.png" className="h-6 w-6" alt="Instagram" />
              <span className="text-sm group-hover:underline">Instagram</span>
            </SmartLink>

            <SmartLink
              to="https://discord.gg/zv2yZvR4YJ"
              newTab
              className="group flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-3 hover:bg-white/10"
            >
              <img src="/images/icons/discord.png" className="h-6 w-6" alt="Discord" />
              <span className="text-sm group-hover:underline">Discord</span>
            </SmartLink>

            <SmartLink
              to="https://tiktok.com/@spellgather"
              newTab
              className="group flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-3 hover:bg-white/10"
            >
              <img src="/images/icons/tiktok.png" className="h-6 w-6" alt="TikTok" />
              <span className="text-sm group-hover:underline">TikTok</span>
            </SmartLink>

            <SmartLink
              to="https://www.facebook.com/profile.php?id=61579819631986"
              newTab
              className="group flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-3 hover:bg-white/10"
            >
              <img src="/images/icons/fb.png" className="h-6 w-6" alt="Facebook" />
              <span className="text-sm group-hover:underline">Facebook</span>
            </SmartLink>

            <SmartLink
              to="https://youtube.com/@spellgather"
              newTab
              className="group flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-3 hover:bg-white/10"
            >
              <img src="/images/icons/youtube.png" className="h-6 w-6" alt="YouTube" />
              <span className="text-sm group-hover:underline">YouTube</span>
            </SmartLink>

            <SmartLink
              to="https://reddit.com/r/spellgather"
              newTab
              className="group flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-3 hover:bg-white/10"
            >
              <img src="/images/icons/reddit.png" className="h-6 w-6" alt="Reddit" />
              <span className="text-sm group-hover:underline">Reddit</span>
            </SmartLink>

            <SmartLink
              to="https://twitch.tv/spellgather"
              newTab
              className="group flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-3 hover:bg-white/10"
            >
              <img src="/images/icons/twitch.png" className="h-6 w-6" alt="Twitch" />
              <span className="text-sm group-hover:underline">Twitch</span>
            </SmartLink>
          </div>

          {/* Lightweight embeds */}
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* YouTube */}
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="aspect-video overflow-hidden rounded-lg">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/4giOweYE2e0"
                  title="Spellgather Promo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <p className="mt-3 text-sm text-white/80">Watch our intro video</p>
            </div>

            {/* Discord — safe server widget (no webhooks) */}
            <DiscordWidgetCard />

            {/* Twitter CTA card */}
            <div className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-600/10 p-4">
              <h3 className="text-lg font-semibold text-fuchsia-200">
                Follow us on X & support our Indiegogo campaign!
              </h3>
              <p className="mt-2 text-sm text-white/80">
                Backers help cover early build & infra costs—and receive a
                special gift while supplies last.
              </p>
              <div className="mt-3 space-y-3">
                {/* Embedded timeline */}
                <a
                  className="twitter-timeline"
                  data-height="380"
                  data-theme="dark"
                  data-chrome="noheader nofooter noborders transparent"
                  href="https://twitter.com/spellgather"
                >
                  Tweets by @spellgather
                </a>

                {/* Optional: follow button */}
                <a
                  href="https://twitter.com/spellgather?ref_src=twsrc%5Etfw"
                  className="twitter-follow-button"
                  data-show-count="false"
                  data-size="large"
                >
                  Follow @spellgather
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Card pile with pixel rats — sits near the bottom of the landing hero */}
        <div className="mt-72 flex justify-center ">
          <div className="relative w-full max-w-4xl h-40 select-none" aria-hidden="true">
            {/* Decorative style / animations */}
            <style>{`
              /* pile container */
              .card-pile { position:absolute; left:50%; transform:translateX(-50%); bottom:0; width:86%; height:240px; pointer-events:none; }

              /* small pixel trading cards (burst style) */
              .card-pile .pile-card {
                position:absolute;
                width:10px;
                height:13px;
                border-radius:1px;
                border:2px solid #111827;
                background:#fca5a5;
                box-shadow: 0 3px 6px rgba(0,0,0,0.55);
                transform-origin:center;
                image-rendering: pixelated;
                will-change: transform, opacity;
              }
              .card-pile .pile-card:nth-child(4n+2) { background:#fde68a; }
              .card-pile .pile-card:nth-child(4n+3) { background:#86efac; }
              .card-pile .pile-card:nth-child(4n+4) { background:#93c5fd; }

              /* subtle idle jiggle on a per-card basis */
              @keyframes card-jiggle {
                0% { transform: translateY(0) rotate(var(--rot,0deg)) scale(var(--scale,1)) scaleX(var(--flip,1)); }
                50% { transform: translateY(-1px) rotate(calc(var(--rot,0deg) + 1deg)) scale(calc(var(--scale,1) * 1.01)) scaleX(var(--flip,1)); }
                100% { transform: translateY(0) rotate(var(--rot,0deg)) scale(var(--scale,1)) scaleX(var(--flip,1)); }
              }
              .card-pile .pile-card { animation: card-jiggle 6s ease-in-out infinite; }

              /* pile shadow (wider for hill shape) */
              .pile-shadow {
                position:absolute; left:50%; transform:translateX(-50%); bottom:6px;
                width:92%; height:26px; background:radial-gradient(ellipse at center, rgba(0,0,0,0.75), rgba(0,0,0,0.12));
                filter: blur(8px); opacity:0.9; border-radius:999px;
              }

              /* rats (unchanged) */
              .rat {
                position:absolute;
                bottom:18px;
                width:22px;
                height:14px;
                transform-origin:center;
                image-rendering: pixelated;
                filter: drop-shadow(0 1px 0 rgba(0,0,0,0.5));
                pointer-events: none;
              }
              .rat .body { position:absolute; left:3px; top:3px; width:14px; height:7px; background:#6b4e2a; border:2px solid #111; border-radius:2px; }
              .rat .ear { position:absolute; left:2px; top:0; width:3px; height:3px; background:#8b5a3c; border-radius:1px; }
              .rat .tail { position:absolute; right:-6px; top:4px; width:6px; height:2px; background:#3b2a1a; border-radius:2px; transform-origin:left center; }
              .rat .mouth { position:absolute; left:1px; top:5px; width:7px; height:4px; overflow:visible; }
              /* the grabbed card in the mouth */
              .rat .grab {
                position:absolute;
                width:10px;
                height:13px;
                border:2px solid #111827;
                border-radius:1px;
                background:#fde68a;
                opacity:0;
                image-rendering: pixelated;
                transform-origin:center;
              }

              /* rat motion across pile — randomized by inline animationDelay */
              @keyframes rat-run-1 {
                0% { left: -12%; transform: translateY(0) scaleX(1); opacity:1; }
                25% { left: 24%; transform: translateY(-6px) scaleX(1); }
                50% { left: 52%; transform: translateY(-4px) scaleX(1); }
                75% { left: 84%; transform: translateY(-2px) scaleX(1); }
                100% { left:110%; transform: translateY(0) scaleX(1); opacity:0; }
              }
              @keyframes rat-run-2 {
                0% { left: 110%; transform: translateY(0) scaleX(-1); opacity:0; }
                20% { left: 86%; transform: translateY(-2px) scaleX(-1); opacity:1; }
                45% { left: 56%; transform: translateY(-6px) scaleX(-1); }
                70% { left: 20%; transform: translateY(-4px) scaleX(-1); }
                100% { left:-12%; transform: translateY(0) scaleX(-1); opacity:0; }
              }

              .rat.r1 { animation: rat-run-1 var(--rat-dur,10s) linear infinite; z-index:60; }
              .rat.r2 { animation: rat-run-2 var(--rat-dur,12s) linear infinite; z-index:62; }

              /* mouth-grab animation: appear while in the pile then fly away */
              @keyframes mouth-grab {
                0%   { opacity: 0; transform: translate(0,0) scale(0.9) rotate(0deg); }
                18%  { opacity: 1; transform: translate(0,-6px) scale(1.05) rotate(-8deg); }
                40%  { opacity: 1; transform: translate(12px,-10px) scale(1) rotate(-18deg); }
                70%  { opacity: 1; transform: translate(28px,-6px) scale(0.9) rotate(-28deg); }
                100% { opacity: 0; transform: translate(48px,0) scale(0.7) rotate(-40deg); }
              }
              .rat .grab { animation: mouth-grab var(--rat-dur,10s) linear infinite; }

              /* reduce motion for users who prefer it */
              @media (prefers-reduced-motion: reduce) {
                .rat, .card-pile .pile-card { animation: none !important; }
                .rat .grab { opacity: 0 !important; }
              }
            `}</style>

            <div className="card-pile" aria-hidden="true">
              <div className="pile-shadow" />

              {/* mountain-style horde */}
              {(() => {
                const cols = 48;
                const nodes: JSX.Element[] = [];
                const center = 43;
                const hillPeak = 140;
                const cardStep = 9;

                for (let col = 0; col < cols; col++) {
                  const rc = Math.abs(Math.sin((col + 3) * 12.9898));
                  const rrot = Math.abs(Math.sin((col + 11) * 78.233));
                  const spread = col / (cols - 1) - 0.5;
                  const leftPct = Math.floor(center + spread * 86);
                  const dist = Math.abs(leftPct - center) / center;
                  const peakAtX = Math.floor((1 - Math.pow(dist, 1.8)) * hillPeak) + 4;
                  const layers = Math.max(3, Math.ceil(peakAtX / cardStep));

                  for (let layer = 0; layer < layers; layer++) {
                    const layerRatio = layer / (layers - 1 || 1);
                    const bias = Math.pow(layerRatio, 0.9);
                    const bottomPx = Math.floor(bias * peakAtX);
                    const r1 = Math.abs(Math.sin((col * 7 + layer * 13) * 0.123));
                    const r2 = Math.abs(Math.cos((col * 11 + layer * 5) * 0.091));
                    const rotDeg = Math.floor((rrot * 2 + r1) * 80) - 40 + Math.floor((layer % 3) * 6);
                    const scale = 0.85 + r2 * 0.5;
                    const flip = r1 > 0.92 ? -1 : 1;
                    const xNudge = Math.floor((r2 - 0.5) * 10);
                    const zIndex = 500 + bottomPx + layer;
                    const opacity = 0.76 + (r1 - 0.5) * 0.28;

                    const style: React.CSSProperties = {
                      left: `${leftPct}%`,
                      bottom: `${bottomPx}px`,
                      zIndex,
                      opacity,
                      ["--rot" as any]: `${rotDeg}deg`,
                      ["--scale" as any]: `${scale}`,
                      ["--flip" as any]: `${flip}`,
                      transform: `translateX(${xNudge}px) rotate(${rotDeg}deg) scale(${scale}) scaleX(${flip})`,
                    };

                    nodes.push(
                      <div key={`horde-${col}-${layer}`} className="pile-card" style={style} />,
                    );
                  }
                }

                return nodes;
              })()}

              {/* rats */}
              {(() => {
                const d1 = Math.floor(Math.random() * 6) + 1;
                const d2 = Math.floor(Math.random() * 6) + 3;
                return (
                  <>
                    <div
                      className="rat r1"
                      style={{
                        zIndex: 700,
                        ["--rat-dur" as any]: "10s",
                        animationDelay: `${d1}s`,
                      }}
                      aria-hidden="true"
                    >
                      <div className="ear" />
                      <div className="body" />
                      <div className="tail" />
                      <div className="mouth">
                        <div className="grab" />
                      </div>
                    </div>

                    <div
                      className="rat r2"
                      style={{
                        zIndex: 702,
                        ["--rat-dur" as any]: "12s",
                        animationDelay: `${d2}s`,
                      }}
                      aria-hidden="true"
                    >
                      <div className="ear" />
                      <div className="body" />
                      <div className="tail" />
                      <div className="mouth">
                        <div className="grab" />
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        <p className="mt-36 text-sm text-white/60">
          Already exploring?{" "}
          <Link className="underline" to="/dashboard">
            Go to your dashboard
          </Link>
        </p>
      </section>

      {/* Sign Up Modal */}
      {signupOpen && (
        <AuthModal
          onClose={() => setSignupOpen(false)}
          header={
            <div className="flex flex-col items-start">
              <img
                src="/images/spellgather-logo-blue.png"
                alt="Spellgather"
                className="mb-3 h-24 sm:h-36 md:h-48 lg:h-52 w-auto"
                loading="eager"
                decoding="async"
              />
              <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight">
                Welcome!
                <p>
                  <span className="text-fuchsia-400">Your journey begins here.</span>
                </p>
              </h1>
              <p> </p>
              <p className="mt-3 text-white/80 max-w-full mx-auto">
                Join Spellgather and step into a realm where Magic: The Gathering
                players unite. Discover allies, forge trades, and gather for
                battles both near and far.
              </p>
              <p>
                By signing up, you&apos;re not just creating an
                account—you&apos;re joining a fellowship bound by spells,
                stories, and the game we love.
              </p>
              <p> </p>
              <p> </p>
              <p className="mt-3 text-xs text-white/80 max-w-full mx-auto">
                Spellgather is not affiliated with or endorsed by Wizards of the
                Coast.
              </p>
            </div>
          }
        >
          <SignUpPanel onDone={() => setSignupOpen(false)} />
        </AuthModal>
      )}

      {/* Login Modal */}
      {loginOpen && (
        <AuthModal
          onClose={() => setLoginOpen(false)}
          header={
            <div className="flex flex-col items-end text-right">
              <img
                src="/images/spellgather-logo.png"
                alt="Spellgather"
                className="mb-3 h-24 sm:h-36 md:h-48 lg:h-52 w-auto"
                loading="eager"
                decoding="async"
              />
              <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight">
                Welcome back,<span className="text-fuchsia-400"> planeswalker.</span>
              </h1>
              <p className="mt-3 text-white/80 max-w-full mx-auto">
                Your path with Spellgather continues—where spells are shared,
                trades are struck, and games await.{" "}
                <p>
                  Step back into the circle of players near and far, and pick up
                  the journey you began.
                </p>
              </p>
              <p></p>
            </div>
          }
        >
          <LoginPanel onDone={() => setLoginOpen(false)} />
        </AuthModal>
      )}

      {/* in your SignUp page component (footer under the form) */}
      <div className="mt-3 text-xs text-slate-400">
        Are you a Local Game Store?{" "}
        <SmartLink
          to="/profile/settings?lgs=1"
          className="text-sky-300 hover:underline"
          onClick={() => {
            try {
              sessionStorage.setItem(
                "afterAuthRedirect",
                "/profile/settings?lgs=1",
              );
            } catch {}
          }}
        >
          Apply for an LGS profile
        </SmartLink>
      </div>
    </main>
  );
}

/* =========================
   UI helpers for the card strip
========================= */

function badgeFor(c: PublicCardTile): JSX.Element {
  const sale = c.for_sale || c.status === "sale";
  const trade = c.for_trade || c.status === "trade";
  if (sale && trade) {
    return (
      <span className="inline-flex items-center rounded border border-cyan-300/40 bg-cyan-400/10 text-cyan-100 px-2 py-[2px] text-[11px]">
        For Sale &amp; Trade
      </span>
    );
  }
  if (sale) {
    return (
      <span className="inline-flex items-center rounded border border-green-300/40 bg-green-400/10 text-green-100 px-2 py-[2px] text-[11px]">
        For Sale
      </span>
    );
  }
  if (trade) {
    return (
      <span className="inline-flex items-center rounded border border-fuchsia-300/40 bg-fuchsia-400/10 text-fuchsia-100 px-2 py-[2px] text-[11px]">
        For Trade
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded border border-white/20 bg-white/5 text-white/70 px-2 py-[2px] text-[11px]">
      Listed
    </span>
  );
}

/* =========================
   Auth panels
========================= */

function SignUpPanel({ onDone }: { onDone: () => void }): JSX.Element {
  const nav = useNavigate();
  const [stage, setStage] = useState<AuthStage>("idle");
  const [err, setErr] = useState<string | null>(null);

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  async function afterAuth(u: User): Promise<void> {
    await ensureUserDoc();

    const url = isNewUser(u)
      ? "/Claim-Username?next=%2Ftermsack&return=%2F"
      : "/dashboard";

    nav(url, { replace: true });
    onDone();
  }

  async function signupGoogle(): Promise<void> {
    setStage("working");
    setErr(null);
    try {
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(auth, provider);
      await afterAuth(cred.user);
    } catch (e: unknown) {
      setErr(parseAuthError(e));
    } finally {
      setStage("idle");
    }
  }

  async function signupEmail(): Promise<void> {
    if (!email.trim() || !password) {
      setErr("Please enter an email and password.");
      return;
    }
    setStage("working");
    setErr(null);
    try {
      const cred = await createUserWithEmailAndPassword(
        auth,
        email.trim(),
        password,
      );
      await afterAuth(cred.user);
    } catch (e: unknown) {
      setErr(parseAuthError(e));
    } finally {
      setStage("idle");
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-white/80 text-sm">
        Choose a sign up method. You’ll claim a permanent username, then review
        our terms, and finish your profile.
      </p>

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => void signupGoogle()}
          disabled={stage === "working"}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-white/10 bg-white/5 px-4 py-2 text-white hover:bg-white/10 disabled:opacity-60"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M21.35 11.1H12v2.9h5.35c-.25 1.5-1.62 4.4-5.35 4.4-3.22 0-5.85-2.66-5.85-5.9s2.63-5.9 5.85-5.9c1.83 0 3.06.78 3.76 1.46l2.56-2.47C16.8 3.3 14.62 2.4 12 2.4 6.97 2.4 2.9 6.47 2.9 11.5S6.97 20.6 12 20.6c6.93 0 8.1-6.08 7.86-9.5z" />
          </svg>
          Continue with Google
        </button>

        <div className="grid grid-cols-1 gap-2">
          <input
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white"
          />
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (min 6 chars)"
            className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white"
          />
          <button
            type="button"
            onClick={() => void signupEmail()}
            disabled={stage === "working"}
            className="rounded-md bg-fuchsia-600 px-4 py-2 text-white font-semibold hover:bg-fuchsia-700 disabled:opacity-60"
          >
            Create account
          </button>
        </div>
      </div>

      {err && <div className="text-amber-300 text-sm">{err}</div>}
      <p className="text-xs text-white/50">
        By continuing, you agree to our{" "}
        <Link className="underline" to="/terms" target="_blank" rel="noreferrer">
          Terms
        </Link>{" "}
        and{" "}
        <Link className="underline" to="/privacy" target="_blank" rel="noreferrer">
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
}

function LoginPanel({ onDone }: { onDone: () => void }): JSX.Element {
  const nav = useNavigate();
  const [stage, setStage] = useState<AuthStage>("idle");
  const [err, setErr] = useState<string | null>(null);
  const [remember, setRemember] = useState<boolean>(true);

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  async function setAuthPersistence(): Promise<void> {
    await setPersistence(
      auth,
      remember ? browserLocalPersistence : browserSessionPersistence,
    );
  }

  async function afterAuth(u: User): Promise<void> {
    nav("/dashboard", { replace: true });
    onDone();
  }

  async function loginGoogle(): Promise<void> {
    setStage("working");
    setErr(null);
    try {
      await setAuthPersistence();
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(auth, provider);
      await afterAuth(cred.user);
    } catch (e: unknown) {
      setErr(parseAuthError(e));
    } finally {
      setStage("idle");
    }
  }

  async function loginEmail(): Promise<void> {
    if (!email.trim() || !password) {
      setErr("Please enter your email and password.");
      return;
    }
    setStage("working");
    setErr(null);
    try {
      await setAuthPersistence();
      const cred = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password,
      );
      await afterAuth(cred.user);
    } catch (e: unknown) {
      setErr(parseAuthError(e));
    } finally {
      setStage("idle");
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-white/80 text-sm">
        Choose how you’d like to log in. You can enable “Remember me” to stay
        signed in.
      </p>

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => void loginGoogle()}
          disabled={stage === "working"}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-white/10 bg-white/5 px-4 py-2 text-white hover:bg-white/10 disabled:opacity-60"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M21.35 11.1H12v2.9h5.35c-.25 1.5-1.62 4.4-5.35 4.4-3.22 0-5.85-2.66-5.85-5.9s2.63-5.9 5.85-5.9c1.83 0 3.06.78 3.76 1.46l2.56-2.47C16.8 3.3 14.62 2.4 12 2.4 6.97 2.4 2.9 6.47 2.9 11.5S6.97 20.6 12 20.6c6.93 0 8.1-6.08 7.86-9.5z" />
          </svg>
          Continue with Google
        </button>

        <div className="grid grid-cols-1 gap-2">
          <input
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white"
          />
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white"
          />
          <button
            type="button"
            onClick={() => void loginEmail()}
            disabled={stage === "working"}
            className="rounded-md bg-indigo-600 px-4 py-2 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
          >
            Log in
          </button>
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-white/80">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          <span>Remember me</span>
        </label>
      </div>

      {err && <div className="text-amber-300 text-sm">{err}</div>}

      <div className="text-xs text-white/50 flex items-center justify-between">
        <Link className="underline" to="/reset-password">
          Forgot password?
        </Link>
        <span>
          New here?{" "}
          <button
            type="button"
            className="underline"
            onClick={() => {
              const click = document.getElementById("landing-cta-signup");
              if (click) (click as HTMLButtonElement).click();
            }}
          >
            Create an account
          </button>
        </span>
      </div>
    </div>
  );
}

/* =========================
   Modal wrapper
========================= */

function AuthModal({
  title,
  header,
  onClose,
  children,
}: {
  title?: string;
  header?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center"
      role="dialog"
      aria-modal="true"
      aria-label={title ?? "Dialog"}
    >
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative z-10 w=[min(720px,92vw)] w-[min(720px,92vw)] rounded-lg border border-gray-700 bg-gray-900 p-4 shadow-xl">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1">
            {header ? (
              header
            ) : (
              <h2 className="text-white font-semibold">{title}</h2>
            )}
          </div>
          <button
            id="landing-open-signup"
            type="button"
            className="btn-pixel btn-pixel-secondary"
            onClick={onClose}
            aria-label="Close"
            title="Close"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Add this component at file scope (outside of the Landing component)
function DiscordWidgetCard(): JSX.Element {
  const guildId = "1418721872643756114";
  const [data, setData] = useState<null | {
    name: string;
    presence_count: number;
    instant_invite?: string;
  }>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    // Fetch lightweight JSON (requires server widget enabled)
    fetch(`https://discord.com/api/guilds/${guildId}/widget.json`)
      .then(r => {
        if (!r.ok) throw new Error("Widget disabled");
        return r.json();
      })
      .then(j => { if (alive) setData(j); })
      .catch(e => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, [guildId]);

  const inviteHref = data?.instant_invite || "https://discord.gg/btRWEaFr";

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white/90 truncate">
            {data?.name || "Spellgather Discord"}
          </p>
          <p className="text-xs text-white/60">
            {data
              ? `${data.presence_count} online`
              : err
              ? "Widget not enabled"
              : "Loading…"}
          </p>
        </div>
        <a
          href={inviteHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center rounded-lg bg-fuchsia-500 px-3 py-2 text-sm font-semibold text-black hover:bg-fuchsia-400"
        >
          Join Server
        </a>
      </div>

      {/* Responsive wrapper: keeps aspect on mobile, fixed widget size on md+ */}
      <div className="rounded-lg overflow-hidden bg-black/40 relative">
        <div className="hidden md:block">
          <iframe
            src={`https://canary.discord.com/widget?id=${guildId}&theme=dark`}
            width="350"
            height="500"
            sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
            frameBorder={0}
            loading="lazy"
            title="Discord Server Widget"
            className="block"
            style={{ maxWidth: "100%", background: "transparent" }}
          />
        </div>
        {/* Mobile: maintain 16/10 aspect and scale iframe to fit */}
        <div className="md:hidden relative w-full overflow-hidden" style={{ paddingTop: "62.5%" }}>
          <iframe
            src={`https://canary.discord.com/widget?id=${guildId}&theme=dark`}
            sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
            frameBorder={0}
            loading="lazy"
            title="Discord Server Widget"
            className="absolute inset-0 w-full h-full scale-[0.9] origin-top"
            style={{ background: "transparent" }}
          />
        </div>
      </div>

      <p className="mt-2 text-sm text-white/80">
        Jump into chat
        {err && <span className="text-amber-300 ml-2">(Fallback view)</span>}
      </p>
    </div>
  );
}

